import React, { useState, useEffect } from 'react'
import { HexColorPicker } from 'react-colorful'
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  ClickAwayListener,
  Typography,
  styled,
} from '@mui/material'

interface ColorPickerProps {
  color: string
  onChange: (color: string) => void
  label: string
  fullWidth?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: string
}

const ColorPickerContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-block',
  width: '100%',
}))

const ColorSwatch = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: 24,
  height: 24,
  borderRadius: 4,
  backgroundColor: color || theme.palette.grey[200],
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  boxShadow: theme.shadows[1],
  position: 'relative',
  '&:hover': {
    boxShadow: theme.shadows[2],
  },
  // Show a pattern for empty colors
  ...(color === '' && {
    backgroundColor: 'transparent',
    backgroundImage: `linear-gradient(45deg, ${theme.palette.grey[300]} 25%, transparent 25%), 
                      linear-gradient(-45deg, ${theme.palette.grey[300]} 25%, transparent 25%), 
                      linear-gradient(45deg, transparent 75%, ${theme.palette.grey[300]} 75%), 
                      linear-gradient(-45deg, transparent 75%, ${theme.palette.grey[300]} 75%)`,
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
  }),
}))

const PopoverContainer = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: theme.zIndex.modal,
  padding: theme.spacing(2),
  marginTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8],
  minWidth: 200,
}))

const StyledColorPicker = styled(HexColorPicker)(({ theme }) => ({
  '& .react-colorful__saturation': {
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  },
  '& .react-colorful__hue': {
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  },
  '& .react-colorful__pointer': {
    width: 18,
    height: 18,
  },
}))

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  label,
  fullWidth = true,
  disabled = false,
  error = false,
  helperText,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(color)

  useEffect(() => {
    setInputValue(color)
  }, [color])

  const handleColorChange = (newColor: string) => {
    setInputValue(newColor)
    onChange(newColor)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputValue(value)

    // Validate hex color format or allow empty
    if (value === '' || /^#[0-9A-F]{6}$/i.test(value)) {
      onChange(value)
    }
  }

  const handleInputBlur = () => {
    // If invalid hex and not empty, revert to last valid color
    if (inputValue !== '' && !/^#[0-9A-F]{6}$/i.test(inputValue)) {
      setInputValue(color)
    }
  }

  const handleSwatchClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleClickAway = () => {
    setIsOpen(false)
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <ColorPickerContainer>
        <TextField
          fullWidth={fullWidth}
          label={label}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder="#FFFFFF"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ColorSwatch color={color} onClick={handleSwatchClick} />
              </InputAdornment>
            ),
          }}
        />

        {isOpen && (
          <PopoverContainer>
            <Typography variant="subtitle2" gutterBottom>
              {label}
            </Typography>
            <StyledColorPicker color={color || '#2196F3'} onChange={handleColorChange} />
            <Box mt={2}>
              <TextField
                size="small"
                label="Hex"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                fullWidth
                inputProps={{
                  style: { textTransform: 'uppercase' },
                }}
              />
            </Box>
          </PopoverContainer>
        )}
      </ColorPickerContainer>
    </ClickAwayListener>
  )
}

export default ColorPicker
